import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, css, html, nothing } from "lit";
import { property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { styleMap } from "lit/directives/style-map.js";
//#region src/pd-timeline/PdTimeline.ts
/**
* Timeline/stepper component for displaying sequential steps or progress.
*
* @tagname pd-timeline
* @summary Timeline stepper for sequential steps or progress display.
*
* @event step-click - Fired when a clickable point is clicked. Emits `{index, id}`.
*
* @slot label-[index] - Named slots for custom label content at each point.
*
* @csspart label - The label container element for each timeline point.
*
* @cssprop --pd-timeline-line-color - Connecting line color.
* @cssprop --pd-timeline-line-width - Connecting line width.
* @cssprop --pd-timeline-line-style - Connecting line style.
* @cssprop --pd-timeline-point-size - Point circle size.
* @cssprop --pd-timeline-point-bg - Point background color.
* @cssprop --pd-timeline-point-color - Point text color.
* @cssprop --pd-timeline-point-border-color - Point border color.
* @cssprop --pd-timeline-point-border-width - Point border width.
* @cssprop --pd-timeline-point-font-size - Point text size.
* @cssprop --pd-timeline-label-gap - Gap between point and label.
* @cssprop --pd-timeline-label-max-width - Maximum label width.
* @cssprop --pd-timeline-vertical-row-gap - Vertical layout row gap.
* @cssprop --pd-timeline-vertical-block-size - Vertical layout height.
* @cssprop --pd-timeline-item-color - Per-item accent color (set via PointItem.color, overrides point-bg and point-border-color).
* @cssprop --pd-timeline-point-max-text-width - Max text width / circle diameter (default: 10ch).
* @cssprop --pd-timeline-color-active - Active state color.
* @cssprop --pd-timeline-color-done - Done state color.
* @cssprop --pd-timeline-color-error - Error state color.
*/
var PdTimeline = class extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.orientation = "horizontal";
		this.points = [];
		this.clickable = false;
	}
	static {
		this.styles = css`
    :host {
      display: block;

      /* Line */
      --pd-timeline-line-color: var(--pd-default-col);
      --pd-timeline-line-width: 2px;
      --pd-timeline-line-style: solid;

      /* Point (dot) */
      --pd-timeline-point-size: 28px;
      --pd-timeline-point-bg: var(--pd-default-bg-col);
      --pd-timeline-point-color: var(--pd-default-darkest-col);
      --pd-timeline-point-border-color: var(--pd-default-disabled-col);
      --pd-timeline-point-border-width: 2px;
      --pd-timeline-point-font-size: var(
        --pd-default-font-content-size,
        0.875rem
      );

      /* Label */
      --pd-timeline-label-gap: var(--pd-spacing-sm, 0.5rem);
      --pd-timeline-label-max-width: 20ch;

      /* Vertical layout */
      --pd-timeline-vertical-row-gap: var(--pd-spacing-lg, 2rem);

      /* State colors */
      --pd-timeline-color-active: var(--pd-default-info-col);
      --pd-timeline-color-done: var(--pd-default-success-col);
      --pd-timeline-color-error: var(--pd-default-error-col);
    }

    /* === Common container === */
    .rail {
      position: relative;
    }

    /* === Horizontal layout === */
    :host([orientation="horizontal"]) .rail {
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: 1fr;
      align-items: center;
      gap: 0;
      padding-block: calc(
        var(--pd-timeline-point-size) * 0.5
      ); /* Platz für Labels oben/unten */
    }

    :host([orientation="horizontal"]) .rail::before {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      border-top: var(--pd-timeline-line-width) var(--pd-timeline-line-style)
        var(--pd-timeline-line-color);
      z-index: 0;
    }

    /* === Vertical layout === */

    :host([orientation="vertical"]) {
      /* default: auto, kann außen überschrieben werden */
      block-size: var(--pd-timeline-vertical-block-size, auto);
      --pd-timeline-label-max-width: 100%;
    }

    :host([orientation="vertical"]) .rail {
      block-size: 100%;
      display: grid;
      grid-template-rows: repeat(var(--_count, 1), auto);
      row-gap: var(--pd-timeline-vertical-row-gap);
      justify-items: start;
      align-content: space-around;
      padding-inline: var(--pd-spacing-md, 1rem);
      padding-block: var(--pd-spacing-xs, 0.25rem);
    }

    :host([orientation="vertical"]) .rail::before {
      content: "";
      position: absolute;
      top: 0;
      bottom: 0;
      left: calc(
        var(--pd-spacing-md, 1rem) + (var(--pd-timeline-point-size) * 0.5)
      );
      transform: translateX(-50%);
      border-left: var(--pd-timeline-line-width) var(--pd-timeline-line-style)
        var(--pd-timeline-line-color);
      z-index: 0;
    }

    /* === Item === */
    .item {
      position: relative;
      z-index: 1;
      display: grid;
    }

    :host([orientation="horizontal"]) .item {
      justify-items: center;
      align-items: center;
    }

    /* hack: special case timeline-wizard, labels shoud grow */
    :host([orientation="vertical"]) .item {
      display: flex;
      align-items: baseline;
      gap: var(--pd-timeline-label-gap);
      width: 100%;
    }

    /* === Dot (point) === */
    .dot {
      box-sizing: border-box;
      display: block;
      width: fit-content;
      min-width: var(--pd-timeline-point-size);
      max-width: calc(
        var(--pd-timeline-point-max-text-width, 10ch) + 1em +
          var(--pd-timeline-point-border-width) * 2
      );
      aspect-ratio: 1;
      padding: 0.5em;
      border-radius: 999px;
      overflow: hidden;
      background: var(--pd-timeline-item-color, var(--pd-timeline-point-bg));
      color: var(--pd-timeline-point-color);
      border: var(--pd-timeline-point-border-width) solid
        var(--pd-timeline-item-color, var(--pd-timeline-point-border-color));
      text-align: center;
      font-size: var(--pd-timeline-point-font-size);
      line-height: 1;
      user-select: none;
      flex-shrink: 0;
    }

    .dot-text {
      position: relative;
      top: 50%;
      transform: translateY(-50%);
      max-width: var(--pd-timeline-point-max-text-width, 10ch);
      white-space: normal;
      word-break: break-word;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: var(--pd-timeline-point-max-lines, 3);
      overflow: hidden;
    }

    .dot[aria-current="step"] {
      outline: 3px solid
        color-mix(in srgb, var(--pd-timeline-color-active), transparent 60%);
      outline-offset: 2px;
    }

    .dot--button {
      cursor: pointer;
    }

    .dot--button:focus-visible {
      outline: 2px solid var(--pd-focus-ring-col);
      outline-offset: 2px;
    }

    /* State colors (border or inner ring hint) */
    .state-active {
      border-color: var(--pd-timeline-color-active);
    }
    .state-done {
      border-color: var(--pd-timeline-color-done);
    }
    .state-error {
      border-color: var(--pd-timeline-color-error);
    }

    /* === Labels === */
    .label {
      position: absolute;
      max-width: var(--pd-timeline-label-max-width);
      text-align: center;
    }

    :host([orientation="vertical"]) .label {
      text-align: left;
      /* max-width above not work? */
      width: max-content;
    }

    /* Horizontal label placement */
    :host([orientation="horizontal"]) .label.top {
      bottom: calc(100% + var(--pd-timeline-label-gap));
    }
    :host([orientation="horizontal"]) .label.back {
      top: calc(100% + var(--pd-timeline-label-gap));
    }

    /* Vertical label placement */
    :host([orientation="vertical"]) .label.top {
      right: calc(
        50% + var(--pd-timeline-point-size) * 0.5 + var(--pd-timeline-label-gap)
      );
    }

    :host([orientation="vertical"]) .label.back {
      /* Specia use case for timeline-wizard, abel shoud grow corectly */
      position: static;
      width: 100%;
      /* left: calc(var(--pd-timeline-point-size) + var(--pd-timeline-label-gap)); 
      top: calc((var(--pd-timeline-point-size) * 0.5) - 9px); */
    }

    /* Make label wrappers not capture clicks if not interactive */
    .label ::slotted(*) {
      pointer-events: auto;
    }
  `;
	}
	updated() {
		this.style.setProperty("--_count", String(this.points?.length ?? 0));
	}
	_labelClass(idx) {
		return `label ${(this.points[idx]?.labelSide ?? "back") === "top" ? "top" : "back"}`;
	}
	_stateClass(p, idx) {
		const s = p?.state ?? (this.activeIndex === idx ? "active" : "default");
		return s && s !== "default" ? `state-${s}` : "";
	}
	_onClick(idx, id) {
		if (!this.clickable) return;
		this.dispatchEvent(new CustomEvent("step-click", {
			detail: {
				index: idx,
				id
			},
			bubbles: true,
			composed: true
		}));
	}
	_onKeyDown(e, idx, id) {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			this._onClick(idx, id);
		}
	}
	_renderDotContent(p) {
		if (p.icon) return html`<span aria-hidden="true">${p.icon}</span>`;
		if (p.text) return html`<span class="dot-text">${p.text}</span>`;
		return nothing;
	}
	render() {
		const roleAttr = "list";
		const ariaOrientation = this.orientation;
		return html`
      <div
        class="rail"
        role="${roleAttr}"
        aria-orientation="${ariaOrientation}"
      >
        ${this.points.map((p, i) => {
			const isActive = this.activeIndex === i;
			const stateClass = this._stateClass(p, i);
			const labelName = `label-${i}`;
			const itemStyles = p.color ? { "--pd-timeline-item-color": p.color } : {};
			return html`
            <div
              class="item"
              style=${styleMap(itemStyles)}
              role="listitem"
              aria-setsize="${this.points.length}"
              aria-posinset="${i + 1}"
            >
              ${this.clickable ? html`<div
                      class="dot dot--button ${stateClass}"
                      aria-current=${ifDefined(isActive ? "step" : void 0)}
                      role="button"
                      tabindex="0"
                      @click=${() => this._onClick(i, p.id)}
                      @keydown=${(e) => this._onKeyDown(e, i, p.id)}
                      title="${p.text ?? p.icon ?? p.id}"
                    >
                      ${this._renderDotContent(p)}
                    </div>` : html`<div
                      class="dot ${stateClass}"
                      aria-current=${ifDefined(isActive ? "step" : void 0)}
                      title="${p.text ?? p.icon ?? p.id}"
                    >
                      ${this._renderDotContent(p)}
                    </div>`}

              <div class="${this._labelClass(i)}" part="label">
                <slot name="${labelName}"></slot>
              </div>
            </div>
          `;
		})}
      </div>
    `;
	}
};
__decorate([property({ reflect: true })], PdTimeline.prototype, "orientation", void 0);
__decorate([property({ type: Array })], PdTimeline.prototype, "points", void 0);
__decorate([property({ type: Number })], PdTimeline.prototype, "activeIndex", void 0);
__decorate([property({ type: Boolean })], PdTimeline.prototype, "clickable", void 0);
//#endregion
export { PdTimeline };
